import { useEffect, useRef, useCallback } from 'react';
import { throttle } from 'lodash-es';

interface UseItemAutoScrollProps {
  /** 是否正在流式输出 */
  isStreaming: boolean;
  /** 是否已完成 */
  isDone?: boolean;
  /** 是否展开 */
  isOpen: boolean;
}

/**
 * 为每个 accordion item 提供独立的自动滚动能力
 * 规则：
 * 1. 用户在某个元素里面滚动过，就不需要强制滚到底部
 * 2. 只要打开其中一个元素，其他元素保持现状，不能互相干扰，需要独立
 */
// TODO: 待提供更优方案，目前欠佳，不是最优
export const useItemAutoScroll = ({
  isStreaming,
  isOpen,
}: UseItemAutoScrollProps) => {
  // 容器引用
  const containerRef = useRef<HTMLDivElement | null>(null);
  // 用户是否手动滚动过（仅在当前流式期间有效）
  const userHasScrolled = useRef(false);
  // 上一次的展开状态
  const prevIsOpen = useRef(isOpen);

  // 滚动到底部
  const scrollToBottom = useCallback(() => {
    if (!containerRef.current) return;
    const { scrollHeight, clientHeight } = containerRef.current;
    const targetPos = scrollHeight - clientHeight;
    containerRef.current.scrollTo({ top: targetPos, behavior: 'smooth' });
  }, []);

  // 检查是否需要自动滚动
  const checkAndScroll = useCallback(() => {
    if (!containerRef.current || !isOpen) return;
    
    // 如果用户已经手动滚动过，则不自动滚动
    if (userHasScrolled.current) return;

    const { scrollHeight, clientHeight } = containerRef.current;
    
    // 内容超出可视区域时才需要滚动
    if (scrollHeight > clientHeight) {
      scrollToBottom();
    }
  }, [isOpen, scrollToBottom]);

  // 监听展开状态变化
  useEffect(() => {
    // 从关闭到打开时，重置用户滚动标记
    if (!prevIsOpen.current && isOpen) {
      userHasScrolled.current = false;
      // 延迟执行，确保 DOM 已渲染
      requestAnimationFrame(() => {
        checkAndScroll();
      });
    }
    prevIsOpen.current = isOpen;
  }, [isOpen, checkAndScroll]);

  // 监听内容变化（流式输出时）
  useEffect(() => {
    if (!containerRef.current || !isOpen) return;

    const container = containerRef.current;

    // 使用 MutationObserver 监听内容变化
    const observer = new MutationObserver(() => {
      if (isStreaming && !userHasScrolled.current) {
        requestAnimationFrame(checkAndScroll);
      }
    });

    observer.observe(container, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    return () => observer.disconnect();
  }, [isOpen, isStreaming, checkAndScroll]);

  // 监听用户滚动行为
  useEffect(() => {
    if (!containerRef.current || !isOpen) return;

    const container = containerRef.current;

    // 处理滚轮事件 - 只在流式输出期间标记用户干预
    const handleWheel = throttle((e: WheelEvent) => {
      // 只在流式输出期间，用户主动滚动时标记为已干预
      if (isStreaming && Math.abs(e.deltaY) > 0) {
        userHasScrolled.current = true;
      }
    }, 100);

    container.addEventListener('wheel', handleWheel as unknown as EventListener);

    return () => {
      container.removeEventListener('wheel', handleWheel as unknown as EventListener);
    };
  }, [isOpen, isStreaming]);

  return {
    containerRef,
    scrollToBottom,
  };
};
